import { useCallback, useMemo } from 'react'
import { useExport } from './useExport'
import {
  createCommitmentLetter,
  getCommitmentLetterExport,
  type CommitmentLetterGenerateRequest,
} from '@/api/commitmentLetters'
import { toast } from '@/stores/useToastStore'

const MAX_LINE_ITEMS = 500

/**
 * Hook to export a commitment letter for selected plan lines.
 * Uses the generic useExport hook with commitment-letter-specific endpoints.
 */
export function useCommitmentLetterExport(
  planId: string,
  versionId: string | null
) {
  const createFn = useCallback(
    (request?: CommitmentLetterGenerateRequest) =>
      createCommitmentLetter(planId, versionId!, request!),
    [planId, versionId]
  )

  const getFn = useCallback(
    (exportId: string) => getCommitmentLetterExport(exportId),
    []
  )

  const exportHook = useExport<CommitmentLetterGenerateRequest>({
    createExportFn: createFn,
    getExportFn: getFn,
    filenamePrefix: 'commitment-letter',
    fileExtension: 'zip',
  })

  const { startExport } = exportHook

  const exportCommitmentLetter = useCallback(
    (lineItemIds: string[], dueDate: string) => {
      if (!versionId) return

      if (lineItemIds.length > MAX_LINE_ITEMS) {
        toast.error(
          `Cannot create commitment letter for more than ${MAX_LINE_ITEMS} items`
        )
        return
      }

      startExport({ line_item_ids: lineItemIds, due_date: dueDate })
    },
    [versionId, startExport]
  )

  const canExport = useMemo(() => !!versionId, [versionId])

  return {
    ...exportHook,
    exportCommitmentLetter,
    canExport,
  }
}
